import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { SessionStorageService } from './session-storage.service';
import { BaseApiService } from './base-api.service';

export interface SendTokenRequest {
  phoneNumber: string;
  stepName: string | undefined;
  propostaUUID: string | undefined;
}

export interface ValidateTokenRequest {
  phoneNumber: string;
  token: string;
  nomeEtapa: string | undefined;
}

export interface TokenDispatcherResponse {
  success?: boolean;
  message?: string;
  status?: string;
  errors?: string[];
  data?: {
    message?: string;
    status?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class TokenDispatcherService extends BaseApiService {

  constructor(
    private httpClient: HttpClient,
    sessionStorageService: SessionStorageService
  ) {
    super('tokendispatcher', sessionStorageService);
  }

  /**
   * Envia o token SMS para o telefone
   * POST /tokendispatcher/token
   */
  sendToken(phoneNumber: string, stepName: string | undefined, propostaUUID: string | undefined): Observable<{ success: boolean; message?: string }> {
    const request: SendTokenRequest = { phoneNumber, stepName, propostaUUID};
    const url = `${this.baseUrl}/token`;
    
    return this.httpClient.post<TokenDispatcherResponse>(url, request).pipe(
      map((raw) => {
        if (raw?.errors && raw.errors.length > 0) {
          const msg = raw.errors.join(', ');
          return { success: false, message: msg };
        }
        
        const msg = "Token reenviado via sms.";

        return { success: true, message: msg };
      }),
      catchError((error) => {
        const msg = this.extractErrorMessage(error);
        throw { success: false, message: msg };
      })
    );
  }

  /**
   * Valida o código SMS através do token dispatcher
   * POST /tokendispatcher/token/validate
   */
  validateToken(phoneNumber: string, token: string, nomeEtapa: string | undefined): Observable<{ success: boolean; message?: string }> {
    const request: ValidateTokenRequest = { phoneNumber, token, nomeEtapa};
    const url = `${this.baseUrl}/token/validate`;
    
    return this.httpClient.post<TokenDispatcherResponse>(url, request).pipe(
      map((raw) => {
        if (raw?.errors && raw.errors.length > 0) {
          const msg = raw.errors.join(', ');
          return { success: false, message: msg };
        }

        const msg =
          raw?.message ??
          raw?.data?.message ??
          raw?.data?.status ??
          raw?.status ??
          '';

        const success =
          /success/i.test(msg) ||
          /valid/i.test(msg) ||
          /sucesso/i.test(msg) ||
          /válid/i.test(msg);

        return { success, message: msg };
      }),
      catchError((error) => {
        const msg = this.extractErrorMessage(error);
        throw { success: false, message: msg };
      })
    );
  }

  private extractErrorMessage(err: any): string {
    const data = err?.error ?? err;

    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      return data.errors.join(', ');
    }
    if (typeof data?.errors === 'string' && data.errors.trim()) {
      return data.errors;
    }
    if (typeof data === 'object' && (data?.message || data?.detail)) {
      return data.message || data.detail;
    }
    if (err?.message) return err.message;
    if (err?.statusText) return err.statusText;
    return 'Erro ao processar a solicitação';
  }
}